// @ts-nocheck
import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useTeams, useTeam, useTeamRecurringFeedback, useTeamRecurringFeedbackHistory, useTeamOneOnOneSetups, useTeamOneOnOneSessions, useAddTeamMember, useRemoveTeamMember, useOrganizationMembers, useSendTeamRecurringFeedbackNow, useSendTeamRecurringFeedbackParticipant, useToggleTeamRecurringFeedback, useDeleteTeamRecurringFeedback, useCreateOneOnOneSession, useUpdateOneOnOneSetup, useManualSendOneOnOneSetup } from '@/hooks/api-hooks';
import { toast } from 'react-hot-toast';
import { EditTeamModal } from '@/components/modals/EditTeamModal';
import { DeleteTeamModal } from '@/components/modals/DeleteTeamModal';
import { CreateOneOnOneSetupModal } from '@/components/modals/CreateOneOnOneSetupModal';
import { EditOneOnOneSetupModal } from '@/components/modals/EditOneOnOneSetupModal';
import { DeleteOneOnOneSetupModal } from '@/components/modals/DeleteOneOnOneSetupModal';
import { CreateOneOnOneSessionModal } from '@/components/modals/CreateOneOnOneSessionModal';
import { EditOneOnOneSessionModal } from '@/components/modals/EditOneOnOneSessionModal';
import { DeleteOneOnOneSessionModal } from '@/components/modals/DeleteOneOnOneSessionModal';
import { ViewOneOnOneSessionsModal } from '@/components/modals/ViewOneOnOneSessionsModal';
import { ReassignMemberModal } from '@/components/modals/ReassignMemberModal';
import { EditRoleModal } from '@/components/modals/EditRoleModal';
import { RemoveFromTeamModal } from '@/components/modals/RemoveFromTeamModal';
import { Setup1on1Modal } from '@/components/modals/Setup1on1Modal';
import { AddTeamMemberModal } from '@/components/modals/AddTeamMemberModal';
import { EditMemberModal } from '@/components/modals/EditMemberModal';
import { CreateRecurringFeedbackModal } from '@/components/modals/CreateRecurringFeedbackModal';
import type { ExistingRecurringFeedbackCycle } from '@/components/modals/CreateRecurringFeedbackModal';
import { SendRecurringFeedbackParticipantModal } from '@/components/modals/SendRecurringFeedbackParticipantModal';
import { RecurringFeedbackHistoryModal } from '@/components/modals/RecurringFeedbackHistoryModal';

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DataTable } from "@/components/common/DataTable";
import { SessionsGrid } from "@/components/common/SessionsGrid";
import type { OneOnOneSessionWithUsers } from '@/types';
import { generateConductUrl, getAvatarColor, getInitials, cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Code,
  Users,
  Calendar,
  MessageSquare,
  Eye,
  MoreHorizontal,
  PlusCircle,
  RefreshCw,
  UserPlus,
  Shield,
  CheckCircle,
  AlertCircle,
  Video,
  Clock,
  Edit3,
  Trash2,
  ChevronLeft,
  Mail,
  Plus,
  Pause,
  PauseCircle,
  Send,
  PlayCircle,
  CalendarClock,
} from "lucide-react";

interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  roleLabel: string;
  status: string;
  temperatureRating?: number;
  joinedAt: string;
}

type RecurringFeedback = ExistingRecurringFeedbackCycle & {
  description?: string | null;
  participants: Array<ExistingRecurringFeedbackCycle['participants'][number] & { invitedAt?: string }>;
  cronSchedule?: string | null;
  isActive: boolean;
  isPaused?: boolean;
  nextRunDate: string | null;
  lastSentAt?: string | null;
  schedule?: (ExistingRecurringFeedbackCycle['schedule'] & { currentRunDate?: string | null }) | null;
  createdAt: string;
};

function formatDateTimeDisplay(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDateDisplay(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

interface OneOnOneSetup {
  id: string;
  title: string;
  participantId: string;
  managerId: string;
  participant?: any;
  manager?: any;
  teamId?: string;
  workflowType: 'email' | 'physical';
  frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'manual' | 'custom';
  duration: number;
  questions?: Array<{
    id: string;
    text: string;
    type: string;
    isRequired: boolean;
    options: string[];
  }>;
  defaultAgenda: string[];
  isActive: boolean;
  nextScheduledDate?: string;
  startDate?: string;
  startTime?: string;
  createdAt: string;
  updatedAt?: string;
  emailDueDays?: number | null;
}

interface OneOnOneSession {
  id: string;
  setupId: string;
  teamId?: string;
  participantId: string;
  managerId: string;
  scheduledDate: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled' | 'missed';
  duration: number;
  title?: string;
  deliveryMethod: 'email' | 'physical';
  agenda: string[];
  notes?: string;
  actionItems?: string[];
  createdAt: string;
  completedAt?: string;
  mood?: number;
  productivityRating?: number;
  teamTempRating?: number;
  dueInDays?: number | null;
  dueDate?: string | null;
  conductLink?: string;
}

export function TeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const activeTab = searchParams.get('tab') || 'members';
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // One-on-One Setup modals
  const [isCreateOneOnOneSetupModalOpen, setIsCreateOneOnOneSetupModalOpen] = useState(false);
  const [isEditOneOnOneSetupModalOpen, setIsEditOneOnOneSetupModalOpen] = useState(false);
  const [isDeleteOneOnOneSetupModalOpen, setIsDeleteOneOnOneSetupModalOpen] = useState(false);
  const [selectedOneOnOneSetup, setSelectedOneOnOneSetup] = useState<OneOnOneSetup | null>(null);

  // One-on-One Session modals
  const [isCreateOneOnOneSessionModalOpen, setIsCreateOneOnOneSessionModalOpen] = useState(false);
  const [isEditOneOnOneSessionModalOpen, setIsEditOneOnOneSessionModalOpen] = useState(false);
  const [selectedOneOnOneSession, setSelectedOneOnOneSession] = useState<OneOnOneSession | null>(null);
  const [preselectedSetupForSession, setPreselectedSetupForSession] = useState<OneOnOneSetup | null>(null);
  const [viewSessionsModal, setViewSessionsModal] = useState<{ open: boolean; setup: OneOnOneSetup | null }>({ open: false, setup: null });
  const [sessionToDelete, setSessionToDelete] = useState<OneOnOneSessionWithUsers | null>(null);
  const createSessionFromSetupMutation = useCreateOneOnOneSession();
  const updateSetupStatusMutation = useUpdateOneOnOneSetup();
  const manualSendSetupMutation = useManualSendOneOnOneSetup();

  // Team member action modals
  const [editProfileModal, setEditProfileModal] = useState<{ open: boolean; member: any | null }>({ open: false, member: null });
  const [reassignModal, setReassignModal] = useState<{ open: boolean; member: any | null }>({ open: false, member: null });
  const [editRoleModal, setEditRoleModal] = useState<{ open: boolean; member: any | null }>({ open: false, member: null });
  const [oneOnOneModal, setOneOnOneModal] = useState<{ open: boolean; member: any | null }>({ open: false, member: null });
  const [removeModal, setRemoveModal] = useState<{ open: boolean; member: any | null }>({ open: false, member: null });
  const [addMemberModal, setAddMemberModal] = useState(false);
  const [recurringModalState, setRecurringModalState] = useState<{ open: boolean; mode: 'create' | 'edit'; cycle: RecurringFeedback | null }>({ open: false, mode: 'create', cycle: null });
  const [participantModalState, setParticipantModalState] = useState<{ open: boolean; cycle: RecurringFeedback | null }>({ open: false, cycle: null });
  const [historyModalState, setHistoryModalState] = useState<{ open: boolean; cycle: RecurringFeedback | null }>({ open: false, cycle: null });

  const upsertTeamMemberMutation = useAddTeamMember();
  const removeTeamMemberMutation = useRemoveTeamMember();

  const { data: teamData, isLoading: isLoadingTeam } = useTeam(id);
  const team = (teamData as any)?.data ?? teamData ?? null;
  const getFallbackAvatarUrl = React.useCallback((seed: string, email?: string) => {
    const base = seed && seed.trim().length > 0 ? seed : email ?? 'member';
    const normalized = base.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const seedValue = encodeURIComponent(normalized || 'member');
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seedValue}`;
  }, []);
  const teamMembers = React.useMemo(() => {
    if (!team) return [];
    const members = Array.isArray(team.members) ? team.members : [];
    return members.map((member: any) => {
      const user = member.user ?? {};
      const firstName = user.firstName ?? user.first_name ?? '';
      const lastName = user.lastName ?? user.last_name ?? '';
      const email = user.email ?? '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ') || email || 'Unknown member';
      const teamRole = (member.role ?? '').toString().toUpperCase();
      const roleLabel = teamRole === 'MANAGER'
        ? 'Manager'
        : teamRole === 'TEAM_LEAD'
        ? 'Team Lead'
        : 'Member';
      const avatar = user.avatar ?? getFallbackAvatarUrl(fullName, email);

      return {
        ...member,
        teamId: member.teamId ?? team.id,
        role: teamRole,
        roleLabel,
        user: {
          ...user,
          id: user.id,
          firstName,
          lastName,
          fullName,
          email,
          avatar,
          department: user.department ?? '',
          jobTitle: user.jobTitle ?? user.job_title ?? '',
          phone: user.phone ?? '',
        },
        avatar,
        id: member.id ?? user.id ?? `${team.id}-${email || Math.random()}`,
        userId: user.id,
        name: fullName,
        status: member.status ?? 'active',
      };
    });
  }, [team, getFallbackAvatarUrl]);
  const isLoadingMembers = isLoadingTeam;

  const getMemberDisplayName = React.useCallback(
    (memberId?: string | null, fallback?: any, defaultLabel: string = 'Member') => {
      if (!memberId) return defaultLabel;

      const matched = teamMembers.find((member: any) => (member?.user?.id ?? member?.id) === memberId);
      if (matched) {
        const user = matched.user ?? matched;
        const firstName = user.firstName ?? user.first_name ?? '';
        const lastName = user.lastName ?? user.last_name ?? '';
        const fullName = user.fullName ?? user.full_name ?? `${firstName} ${lastName}`.trim();
        return fullName || user.email || defaultLabel;
      }

      if (fallback) {
        const user = fallback.user ?? fallback;
        if (user) {
          const firstName = user.firstName ?? user.first_name ?? '';
          const lastName = user.lastName ?? user.last_name ?? '';
          const fullName = user.fullName ?? user.full_name ?? `${firstName} ${lastName}`.trim();
          return fullName || user.email || defaultLabel;
        }
      }

      return defaultLabel;
    },
    [teamMembers],
  );

  const { data: orgTeamsData } = useTeams({ includeMembers: false });
  const organizationTeams = React.useMemo(() => {
    const raw = (orgTeamsData as any)?.teams ?? (orgTeamsData as any)?.data;
    return Array.isArray(raw) ? raw : [];
  }, [orgTeamsData]);

  const { data: orgMembersData } = useOrganizationMembers(
    user?.currentOrganizationId || '',
    { isActive: true },
    { enabled: !!user?.currentOrganizationId }
  );

  const organizationMembers = React.useMemo(() => {
    const raw = (orgMembersData as any)?.data ?? [];
    return Array.isArray(raw)
      ? raw
          .filter((member: any) => !member.isInvitation)
          .map((member: any) => {
            const id = member.user?.id ?? member.id;
            const email = member.user?.email ?? member.email ?? '';
            const name =
              member.user?.name ||
              [member.user?.firstName ?? member.firstName, member.user?.lastName ?? member.lastName]
                .filter(Boolean)
                .join(' ') ||
              email ||
              'Unknown member';

            return { id, name, email };
          })
          .filter((member: any) => member.id)
      : [];
  }, [orgMembersData]);

  const availableMembersForTeam = React.useMemo(() => {
    const memberIds = new Set(teamMembers.map((member: any) => member.userId));
    return organizationMembers.filter((member) => !memberIds.has(member.id));
  }, [organizationMembers, teamMembers]);

  const modalTeamMembers = React.useMemo(() => {
    return teamMembers.map((member: any) => {
      const fullName = member.user?.fullName || member.name || '';
      const email = member.user?.email || member.email || '';
      return {
        id: member.id,
        userId: member.userId,
        name: fullName || email || 'Unknown member',
        email,
      };
    });
  }, [teamMembers]);

  const { data: recurringData, isLoading: isLoadingRecurring } = useTeamRecurringFeedback({ teamId: id });
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
    error: historyError,
    refetch: refetchHistory,
  } = useTeamRecurringFeedbackHistory({
    teamId: id,
    cycleId: historyModalState.cycle?.id,
    enabled: historyModalState.open,
  });
  const historyResponse = (historyData ?? {}) as Record<string, any>;
  const historyPayload = historyResponse?.data ?? null;
  const historyErrorMessage = historyError
    ? historyError instanceof Error
      ? historyError.message
      : 'Failed to load feedback history'
    : historyResponse && historyResponse.success === false && historyResponse.error
    ? historyResponse.error
    : null;
  const historyLoadingState = isHistoryLoading || (isHistoryFetching && historyModalState.open);
  React.useEffect(() => {
    if (historyModalState.open && historyModalState.cycle?.id) {
      refetchHistory().catch(() => {
        // refetch failures are handled by query's error state
      });
    }
  }, [historyModalState.open, historyModalState.cycle?.id, refetchHistory]);
  const recurringResponse = (recurringData ?? {}) as { data?: any[] };
  const recurringFeedback = recurringResponse.data ?? [];
  const totalFeedbackGiven = React.useMemo(() => {
    return recurringFeedback.reduce((count: number, cycle: any) => {
      const participants = Array.isArray(cycle?.participants) ? cycle.participants : [];
      const completedReviewers = participants.filter((participant: any) => {
        const role = (participant?.role || '').toLowerCase();
        return role !== 'recipient' && participant?.isCompleted;
      });
      return count + completedReviewers.length;
    }, 0);
  }, [recurringFeedback]);

  const sendRecurringCycleMutation = useSendTeamRecurringFeedbackNow();
  const sendRecurringParticipantMutation = useSendTeamRecurringFeedbackParticipant();
  const toggleRecurringFeedbackMutation = useToggleTeamRecurringFeedback();
  const deleteRecurringFeedbackMutation = useDeleteTeamRecurringFeedback();


  const { data: setupsData, isLoading: isLoadingSetups } = useTeamOneOnOneSetups({ teamId: id });
  const setupsResponse = (setupsData ?? {}) as { data?: any[] };
  const rawOneOnOneSetups = (setupsResponse.data ?? []) as any[];
  const oneOnOneSetups: OneOnOneSetup[] = rawOneOnOneSetups.map((setup) => {
    const participantId = setup.participantId || setup.employeeId;
    const normalizedQuestions = Array.isArray(setup.questions)
      ? setup.questions.map((question: any) => ({
          id: question.id || crypto.randomUUID(),
          text: question.text || question.question || '',
          type: question.type || question.questionType || 'text',
          isRequired: question.isRequired !== false,
          options: Array.isArray(question.options) ? question.options : [],
        }))
      : [];

    return {
      ...setup,
      participantId,
      title: typeof setup.title === 'string' && setup.title.trim().length > 0 ? setup.title.trim() : '1-on-1 Session',
      workflowType: (setup.workflowType || (setup.type === 'Email' ? 'email' : setup.type === 'Physical' ? 'physical' : undefined) || 'physical') as 'email' | 'physical',
      frequency: (setup.frequency || setup.schedulePattern || 'bi-weekly') as OneOnOneSetup['frequency'],
    defaultAgenda: Array.isArray(setup.defaultAgenda) ? setup.defaultAgenda : Array.isArray(setup.agenda) ? setup.agenda : [],
    nextScheduledDate: setup.nextScheduledDate || setup.nextMeetingDate,
    questions: normalizedQuestions,
    emailDueDays: typeof setup.emailDueDays === 'number' ? setup.emailDueDays : setup.workflowType === 'email' ? 7 : null,
  };
});

  const { data: sessionsData, isLoading: isLoadingSessions } = useTeamOneOnOneSessions({ teamId: id });
  const sessionsResponse = (sessionsData ?? {}) as { data?: any[] };
  const rawOneOnOneSessions = (sessionsResponse.data ?? []) as any[];
  const oneOnOneSessions: OneOnOneSession[] = rawOneOnOneSessions.map((session) => {
    const setupForSession = oneOnOneSetups.find((setup: OneOnOneSetup) => setup.id === session.setupId);
    const emailDueDays = setupForSession?.emailDueDays ?? (setupForSession?.workflowType === 'email' ? 7 : null);
    const dueDateIso = emailDueDays
      ? new Date(new Date(session.scheduledDate).getTime() + emailDueDays * 24 * 60 * 60 * 1000).toISOString()
      : null;

    const deliveryMethod = (session.deliveryMethod || (session.type === 'Email 1-on-1' ? 'email' : 'physical')) as 'email' | 'physical';
    const sessionType = session.type || (deliveryMethod === 'email' ? 'Email 1-on-1' : 'Physical Meeting');

    return {
      ...session,
      title: session.title || setupForSession?.title || sessionType,
      type: sessionType,
      participantId: session.participantId || session.employeeId,
      deliveryMethod,
      agenda: Array.isArray(session.agenda) ? session.agenda : session.agenda ? [session.agenda] : [],
      dueInDays: emailDueDays,
      dueDate: dueDateIso,
    };
  });

  const totalOneOnOneSessions = oneOnOneSessions.length;
  const teamMemberCount = teamMembers.length;

  const sessionsForSelectedSetup = viewSessionsModal.setup
    ? oneOnOneSessions
        .filter((session) => session.setupId === viewSessionsModal.setup?.id)
        .map((session) => ({
          ...session,
          teamId: session.teamId ?? team?.id ?? '',
          title:
            session.title ||
            viewSessionsModal.setup?.title ||
            `${formatFrequencyLabel(viewSessionsModal.setup?.frequency)} ${viewSessionsModal.setup?.workflowType === 'email' ? 'Email' : 'Physical'} 1-on-1`,
          deliveryMethod: session.deliveryMethod || viewSessionsModal.setup?.workflowType || 'physical',
          dueInDays: session.dueInDays ?? viewSessionsModal.setup?.emailDueDays ?? null,
          dueDate:
            session.dueDate ||
            (viewSessionsModal.setup?.emailDueDays
              ? new Date(new Date(session.scheduledDate).getTime() + (viewSessionsModal.setup.emailDueDays ?? 7) * 24 * 60 * 60 * 1000).toISOString()
              : null),
        }))
    : [];

  const modalParticipant = viewSessionsModal.setup
    ? teamMembers.find((member: any) => member.user?.id === (viewSessionsModal.setup?.participantId || (viewSessionsModal.setup as any).employeeId))
    : null;

  const modalManager = viewSessionsModal.setup
    ? teamMembers.find((member: any) => member.user?.id === viewSessionsModal.setup?.managerId)
    : null;

  // Column definitions for DataTable
  const memberColumns = [
    {
      id: 'member',
      header: 'Member',
      accessorKey: 'user',
      cell: (row: any) => {
        const user = row.user ?? {};
        const firstName = user.firstName ?? user.first_name ?? '';
        const lastName = user.lastName ?? user.last_name ?? '';
        const composedName = `${firstName} ${lastName}`.trim();
        const preferredName = user.fullName ?? user.full_name ?? (composedName.length > 0 ? composedName : undefined);
        const fullName = preferredName || user.email || 'Unknown member';
        const avatarUrl = user.avatar ?? row.avatar ?? '';
        const derivedFirst = firstName || fullName.split(' ')[0] || user.email?.charAt(0) || '';
        const derivedLast = lastName || fullName.split(' ')[1] || '';
        const initials = (getInitials(derivedFirst, derivedLast) || derivedFirst.charAt(0) || 'U').toUpperCase();
        const fallbackClasses = cn('font-semibold text-sm', getAvatarColor(derivedFirst || 'U', derivedLast || ''));

        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8 border">
              <AvatarImage src={avatarUrl || undefined} alt={fullName} />
              <AvatarFallback className={fallbackClasses}>{initials}</AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium text-foreground">{fullName}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      }
    },
    {
      id: 'teamRole',
      header: 'Team Role',
      accessorKey: 'roleLabel',
      cell: (row: any) => (
        <Badge variant={row.role === 'MANAGER' ? 'default' : 'secondary'} className="gap-1 capitalize">
          {row.role === 'MANAGER' && <Shield className="h-3 w-3" />}
          {row.roleLabel}
        </Badge>
      )
    },
    {
      id: 'temperature',
      header: 'Temperature',
      accessorKey: 'temperatureRating',
      cell: (row: any) => (
        row.temperatureRating ? (
          <div className="flex items-center gap-2">
            <Progress
              value={row.temperatureRating * 10}
              className={`h-2 w-16 ${
                row.temperatureRating >= 8 ? '[&>div]:bg-red-500' :
                row.temperatureRating >= 6 ? '[&>div]:bg-orange-500' :
                row.temperatureRating >= 4 ? '[&>div]:bg-yellow-500' :
                '[&>div]:bg-blue-500'
              }`}
            />
            <span className="text-sm font-medium">{row.temperatureRating}</span>
            <span className="text-xs text-muted-foreground">
              {row.temperatureRating >= 7 ? 'Hot' : row.temperatureRating >= 5 ? 'Warm' : 'Cold'}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">No data</span>
        )
      )
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (row: any) => (
        <div className="flex items-center gap-2">
          {row.status === 'active' ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-yellow-600" />
          )}
          <span className="text-sm">
            {row.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </div>
      )
    },
    {
      id: 'actions',
      header: '',
      cell: (row: any) => (
        canManageTeamMembers && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="gap-2" onClick={() => handleEditMember(row)}>
                <Edit3 className="h-4 w-4" />
                Edit Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={() => handleReassignMember(row)}>
                <UserPlus className="h-4 w-4" />
                Reassign
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={() => handleEditRole(row)}>
                <Edit3 className="h-4 w-4" />
                Edit Role
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={() => handleSetup1on1(row)}>
                <Video className="h-4 w-4" />
                Setup 1-1
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 text-destructive" onClick={() => handleRemoveMember(row)}>
                <Trash2 className="h-4 w-4" />
                Remove from Team
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      )
    }
  ];

  const handleTabChange = (tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };

  // Next.js-style action handlers
  const handleEditMember = (member: any) => {
    setEditProfileModal({ open: true, member });
  };

  const handleReassignMember = (member: any) => {
    setReassignModal({ open: true, member });
  };

  const handleEditRole = (member: any) => {
    setEditRoleModal({ open: true, member });
  };

  const handleSetup1on1 = (member: any) => {
    console.log('🎯 Setting up 1-on-1 for member:', member);
    setOneOnOneModal({ open: true, member });
  };

  const handleRemoveMember = (member: any) => {
    setRemoveModal({ open: true, member });
  };

  const teamIdForActions = team?.id || id || '';

  const openCreateRecurringFeedbackModal = () => {
    setRecurringModalState({ open: true, mode: 'create', cycle: null });
  };

  const openEditRecurringFeedbackModal = (cycle: RecurringFeedback) => {
    setRecurringModalState({ open: true, mode: 'edit', cycle });
  };

  const closeRecurringFeedbackModal = () => {
    setRecurringModalState({ open: false, mode: 'create', cycle: null });
  };

  const openParticipantModal = (cycle: RecurringFeedback) => {
    setParticipantModalState({ open: true, cycle });
  };

  const closeParticipantModal = () => {
    setParticipantModalState({ open: false, cycle: null });
  };

  const handleSendCycleNow = (cycle: RecurringFeedback) => {
    if (!teamIdForActions) {
      toast.error('Unable to determine team context');
      return;
    }

    sendRecurringCycleMutation.mutate(
      { teamId: teamIdForActions, cycleId: cycle.id },
      {
        onSuccess: (response: any) => {
          const message = response?.message || 'Recurring feedback sent to all participants';
          toast.success(message);
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Failed to send recurring feedback');
        },
      },
    );
  };

  const handleSendParticipant = (participantId: string) => {
    if (!teamIdForActions || !participantModalState.cycle) {
      toast.error('Unable to determine cycle context');
      return;
    }

    sendRecurringParticipantMutation.mutate(
      {
        teamId: teamIdForActions,
        cycleId: participantModalState.cycle.id,
        participantId,
      },
      {
        onSuccess: (response: any) => {
          const message = response?.message || 'Recurring feedback sent to participant';
          toast.success(message);
          closeParticipantModal();
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Failed to send to participant');
        },
      },
    );
  };

  const handleToggleCycleStatus = (cycle: RecurringFeedback) => {
    if (!teamIdForActions) {
      toast.error('Unable to determine team context');
      return;
    }

    const action = cycle.isActive ? 'pause' : 'resume';

    toggleRecurringFeedbackMutation.mutate(
      { teamId: teamIdForActions, cycleId: cycle.id, action },
      {
        onSuccess: (response: any) => {
          const message = response?.message || (action === 'pause' ? 'Recurring feedback paused' : 'Recurring feedback resumed');
          toast.success(message);
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Failed to update recurring feedback status');
        },
      },
    );
  };

  const handleDeleteCycle = (cycle: RecurringFeedback) => {
    if (!teamIdForActions) {
      toast.error('Unable to determine team context');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this recurring feedback? This cannot be undone.');
    if (!confirmed) return;

    deleteRecurringFeedbackMutation.mutate(
      { teamId: teamIdForActions, cycleId: cycle.id },
      {
        onSuccess: (response: any) => {
          toast.success(response?.message || 'Recurring feedback deleted');
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Failed to delete recurring feedback');
        },
      },
    );
  };

  const isRecurringActionPending =
    sendRecurringCycleMutation.isPending ||
    sendRecurringParticipantMutation.isPending ||
    toggleRecurringFeedbackMutation.isPending ||
    deleteRecurringFeedbackMutation.isPending;

  // Role-based permissions
  const userRole = user?.organizationRole;
  const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin';
  // Team-specific permissions
  const userTeamMember = teamMembers.find((member: TeamMember) => member.userId === user?.id);
  const userTeamRole = (userTeamMember?.role ?? '').toUpperCase();
  const isTeamManager = userTeamRole === 'MANAGER';
  const isTeamLead = userTeamRole === 'TEAM_LEAD';

  const canManageTeamMembers = isOwnerOrAdmin || isTeamManager;
  const canManageOneOnOnes = canManageTeamMembers;
  const canViewFeedbackHistory = isOwnerOrAdmin || isTeamManager || isTeamLead;

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteSuccess = () => {
    setIsDeleteModalOpen(false);
    navigate('/team'); // Redirect to teams list after deletion
  };

  // One-on-One Setup handlers
  const handleCreateOneOnOneSetup = () => {
    setIsCreateOneOnOneSetupModalOpen(true);
  };

  const handleEditOneOnOneSetup = (setup: OneOnOneSetup) => {
    setSelectedOneOnOneSetup(setup);
    setIsEditOneOnOneSetupModalOpen(true);
  };

  const handleDeleteOneOnOneSetup = (setup: OneOnOneSetup) => {
    setSelectedOneOnOneSetup(setup);
    setIsDeleteOneOnOneSetupModalOpen(true);
  };

  const handleOneOnOneSetupModalSuccess = () => {
    setIsCreateOneOnOneSetupModalOpen(false);
    setIsEditOneOnOneSetupModalOpen(false);
    setIsDeleteOneOnOneSetupModalOpen(false);
    setSelectedOneOnOneSetup(null);
  };

  // One-on-One Session handlers
  const handleCreateOneOnOneSession = (preselectedSetup?: OneOnOneSetup) => {
    if (preselectedSetup) {
      const participantId = preselectedSetup.participantId || (preselectedSetup as any).employeeId;
      const managerId = preselectedSetup.managerId;
      if (!participantId || !managerId) {
        toast.error('Unable to determine participants for this setup.');
        return;
      }

      const agenda = Array.isArray(preselectedSetup.defaultAgenda) ? preselectedSetup.defaultAgenda : [];
      const duration = preselectedSetup.duration || 30;
      const scheduledDate = new Date().toISOString();

      createSessionFromSetupMutation.mutate(
        {
          teamId: id!,
          setupId: preselectedSetup.id,
          participantId,
          managerId,
          scheduledDate,
          duration,
          agenda,
        },
        {
          onSuccess: (session) => {
            toast.success('1-on-1 session created');
            const conductUrl = generateConductUrl({
              ...session,
              type: session.deliveryMethod === 'email' ? 'Email' : 'Physical Meeting',
            });
            window.open(conductUrl, '_blank', 'noopener');
          },
          onError: (error: any) => {
            console.error('Error creating quick session:', error);
            toast.error(error?.message || 'Failed to create session.');
          },
        },
      );
      return;
    }

    setPreselectedSetupForSession(null);
    setIsCreateOneOnOneSessionModalOpen(true);
  };

  const handleOneOnOneSessionModalSuccess = () => {
    setIsCreateOneOnOneSessionModalOpen(false);
    setIsEditOneOnOneSessionModalOpen(false);
    setSelectedOneOnOneSession(null);
    setPreselectedSetupForSession(null);
  };

  // New handlers for enhanced dropdown functionality
  const handleViewSessions = (setup: OneOnOneSetup) => {
    setViewSessionsModal({ open: true, setup });
  };

  const handleManualSend = (setup: OneOnOneSetup) => {
    if (manualSendSetupMutation.isPending) {
      return;
    }

    if (!team?.id) {
      toast.error('Team data not loaded yet. Please try again.');
      return;
    }

    if (setup.workflowType !== 'email') {
      toast.error('Manual sending is only available for email 1-on-1 workflows.');
      return;
    }

    manualSendSetupMutation.mutate(
      {
        teamId: team.id,
        setupId: setup.id,
      },
      {
        onSuccess: (response: any) => {
          const message = response?.message || 'Manual notification sent';
          const recipients = Array.isArray(response?.data?.notifiedRecipients)
            ? response.data.notifiedRecipients
            : [];
          if (recipients.length > 0) {
            const list = recipients
              .map((recipient: any) => `${recipient.role}${recipient.placeholder ? ' (console)' : ''}`)
              .join(', ');
            const due = recipients[0]?.dueInDays;
            const dueLabel = due ? ` · due in ${due} day${due === 1 ? '' : 's'}` : '';
            toast.success(`${message} (${list}${dueLabel})`);
          } else {
            toast.success(message);
          }
        },
        onError: (error: any) => {
          console.error('Manual send failed:', error);
          const message = error?.response?.data?.error || error?.message || 'Failed to send manual notification';
          toast.error(message);
        },
      },
    );
  };

  const handlePauseSetup = (setup: OneOnOneSetup) => {
    if (!team?.id) {
      toast.error('Team data not loaded yet. Please try again.');
      return;
    }

    if (updateSetupStatusMutation.isPending) {
      return;
    }

    updateSetupStatusMutation.mutate(
      {
        teamId: team.id,
        setupId: setup.id,
        isActive: !setup.isActive,
      },
      {
        onSuccess: (response: any) => {
          const nextState = !setup.isActive;
          const message = response?.message || (nextState ? 'Recurring 1-on-1 resumed' : 'Recurring 1-on-1 paused');
          toast.success(message);
        },
        onError: (error: any) => {
          console.error('Failed to update setup status:', error);
          const message = error?.response?.data?.error || error?.message || 'Failed to update setup status';
          toast.error(message);
        },
      },
    );
  };

  // Helper function to get session count for a setup
  const getSessionCount = (setupId: string) => {
    return oneOnOneSessions.filter((session: any) => session.setupId === setupId).length;
  };

  // Enhanced helper functions for Next.js patterns
  const getSetupStatus = (setup: OneOnOneSetup) => {
    if (!setup.isActive) return 'paused';

    const nextDate = setup.nextScheduledDate ? new Date(setup.nextScheduledDate) : null;
    const now = new Date();

    if (nextDate && nextDate < now) return 'overdue';
    return 'active';
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Active',
          icon: CheckCircle,
          variant: 'default' as const,
          className: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'paused':
        return {
          label: 'Paused',
          icon: Pause,
          variant: 'secondary' as const,
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'overdue':
        return {
          label: 'Overdue',
          icon: AlertCircle,
          variant: 'destructive' as const,
          className: 'bg-red-100 text-red-800 border-red-200'
        };
      default:
        return {
          label: 'Unknown',
          icon: Clock,
          variant: 'outline' as const,
          className: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  };

  const formatRelativeDate = (dateString: string | null) => {
    if (!dateString) return 'Not scheduled';

    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = date.getTime() - now.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Tomorrow';
    if (diffInDays === -1) return 'Yesterday';
    if (diffInDays > 1 && diffInDays <= 7) return `In ${diffInDays} days`;
    if (diffInDays < -1 && diffInDays >= -7) return `${Math.abs(diffInDays)} days ago`;

    return date.toLocaleDateString();
  };

  const formatFrequencyLabel = (value?: string | null) => {
    if (!value) return 'Manual';
    const spaced = String(value).replace(/-/g, ' ');
    return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Calculate metrics for summary cards
  const setupMetrics = React.useMemo(() => {
    const total = oneOnOneSetups.length;
    const active = oneOnOneSetups.filter((setup: any) => getSetupStatus(setup) === 'active').length;
    const paused = oneOnOneSetups.filter((setup: any) => getSetupStatus(setup) === 'paused').length;
    const overdue = oneOnOneSetups.filter((setup: any) => getSetupStatus(setup) === 'overdue').length;

    return { total, active, paused, overdue };
  }, [oneOnOneSetups]);

  // Loading states
  if (isLoadingTeam) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-foreground"></div>
          <span className="text-muted-foreground">Loading team details...</span>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Team Not Found</h1>
          <p className="text-muted-foreground mb-6">The team you're looking for doesn't exist or you don't have access to it.</p>
          <Button variant="outline" onClick={() => window.history.back()}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const IconComponent = Code;

  return (
    <div className="bg-background min-h-screen">
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-card p-6 rounded-lg border border-border">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
              <IconComponent className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">{team.name}</h1>
              <p className="text-muted-foreground mb-2">{team.description}</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>{team.members?.length || 0} members</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full bg-green-500`}></span>
                  <span>Active</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Created {new Date(team.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Team Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">{teamMemberCount}</div>
                <div className="text-muted-foreground text-sm">Team Members</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">
                  {team.members?.length > 0 ?
                    (team.members.reduce((sum: number, member: any) => sum + (member.temperatureRating || 0), 0) / team.members.length).toFixed(1) :
                    '0.0'
                  }
                </div>
                <div className="text-muted-foreground text-sm">Avg Temperature</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">{totalFeedbackGiven}</div>
                <div className="text-muted-foreground text-sm">Total Feedback</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">{totalOneOnOneSessions}</div>
                <div className="text-muted-foreground text-sm">1-on-1 Sessions</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content with Tabs */}
        <div className="bg-card rounded-lg border border-border">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-0">
            <div className="px-6 pt-6">
              <TabsList className="grid w-full max-w-3xl grid-cols-4 h-10 bg-muted rounded-lg p-1">
                <TabsTrigger
                  value="members"
                  className="text-sm data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  Team Members
                </TabsTrigger>
                <TabsTrigger
                  value="recurring"
                  className="text-sm data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  Recurring Feedback
                </TabsTrigger>
                <TabsTrigger
                  value="one-on-one"
                  className="text-sm data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  1-on-1 Setup
                </TabsTrigger>
                <TabsTrigger
                  value="one-on-one-sessions"
                  className="text-sm data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  1-on-1 Sessions
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="members" className="space-y-0">
              <div className="p-6 space-y-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Team Members</h3>
                    <p className="text-muted-foreground">Manage team members, roles, and permissions</p>
                  </div>
                  {canManageTeamMembers && (
                    <div className="flex items-center gap-4">
                      <Button className="gap-2" onClick={() => setAddMemberModal(true)}>
                        <UserPlus className="h-4 w-4" />
                        Add Member
                      </Button>
                    </div>
                  )}
                </div>

                {isLoadingMembers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-border border-t-foreground"></div>
                      <span className="text-muted-foreground">Loading team members...</span>
                    </div>
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-center py-16 bg-muted/30 rounded-lg border border-border">
                    <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-6">
                      <Users className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h4 className="text-xl font-bold text-foreground mb-2">No Team Members</h4>
                    <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                      This team doesn't have any members yet. Add team members to get started.
                    </p>
                    {canManageTeamMembers && (
                      <Button className="gap-2" onClick={() => setAddMemberModal(true)}>
                        <UserPlus className="h-4 w-4" />
                        Add First Member
                      </Button>
                    )}
                  </div>
                ) : (
                  <DataTable
                    columns={memberColumns}
                    data={teamMembers}
                    pagination={true}
                    pageSize={10}
                    filtering={true}
                    sorting={true}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="recurring" className="space-y-0">
              <div className="p-6 space-y-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Recurring Feedback</h3>
                    <p className="text-muted-foreground">Automated feedback collection and team development tracking</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {canManageTeamMembers && (
                      <Button className="gap-2" onClick={openCreateRecurringFeedbackModal}>
                        <PlusCircle className="h-4 w-4" />
                        Create Recurring Feedback
                      </Button>
                    )}
                    {!canManageTeamMembers && (
                      <div className="text-sm text-muted-foreground">
                        Only Team Leads and Admins can create recurring feedback
                      </div>
                    )}
                  </div>
                </div>

                {isLoadingRecurring ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-border border-t-foreground"></div>
                      <span className="text-muted-foreground">Loading recurring feedback...</span>
                    </div>
                  </div>
                ) : (() => {
                  return recurringFeedback.length === 0;
                })() ? (
                  <div className="text-center py-16 bg-muted/30 rounded-lg border border-border">
                    <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-6">
                      <RefreshCw className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h4 className="text-xl font-bold text-foreground mb-2">No Recurring Feedback Yet</h4>
                    <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                      Set up automated feedback collection to gather regular insights and track team development over time.
                    </p>
                    {canManageTeamMembers ? (
                      <Button className="gap-2" onClick={openCreateRecurringFeedbackModal}>
                        <PlusCircle className="h-4 w-4" />
                        Create First Recurring Feedback
                      </Button>
                    ) : (
                      <div className="text-muted-foreground">
                        Contact your Team Lead or Admin to set up recurring feedback
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6">
                    {recurringFeedback.map((cycle: any) => {
                      const targetUser = teamMembers.find((m: any) => m.user?.id === cycle.targetUserId);
                      const scheduleFrequency = cycle.schedule?.frequency;
                      const scheduleDisplay = scheduleFrequency === 'monthly'
                        ? 'Monthly'
                        : scheduleFrequency === 'quarterly'
                        ? 'Quarterly'
                        : scheduleFrequency === 'biweekly'
                        ? 'Bi-weekly'
                        : scheduleFrequency === 'weekly'
                        ? 'Weekly'
                        : 'Custom';
                      const nextRunAt = cycle.schedule?.nextRunDate || cycle.nextRunDate;
                      const lastSentAt = cycle.lastSentAt || cycle.schedule?.currentRunDate || null;
                      const startDateDisplay = formatDateDisplay(cycle.schedule?.startDate || null);
                      const nextRunDisplay = formatDateTimeDisplay(nextRunAt);
                      const lastSentDisplay = formatDateTimeDisplay(lastSentAt);
                      const pauseLabel = cycle.isActive ? 'Pause Cycle' : 'Resume Cycle';
                      const PauseIconComponent = cycle.isActive ? PauseCircle : PlayCircle;
                      const hasReviewerParticipants = Array.isArray(cycle.participants)
                        ? cycle.participants.some((participant: any) => participant.role === 'reviewer')
                        : false;

                      return (
                        <div key={cycle.id} className="bg-background p-6 rounded-lg border border-border hover:border-gray-300 transition-colors">
                          <div className="flex items-start justify-between mb-6">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${cycle.isActive ? 'bg-green-100' : 'bg-muted'}`}>
                                <RefreshCw className={`h-6 w-6 ${cycle.isActive ? 'text-green-600' : 'text-muted-foreground'}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="text-lg font-bold text-foreground">{cycle.name}</h4>
                                  {cycle.isActive ? (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-md">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 bg-muted text-foreground text-xs font-medium rounded-md">
                                      Paused
                                    </span>
                                  )}
                                </div>
                                <p className="text-muted-foreground mb-4">{cycle.description}</p>
                                <div className="grid gap-3 text-sm text-muted-foreground md:grid-cols-2 mb-4">
                                  <div className="flex items-start gap-2">
                                    <CalendarClock className="mt-0.5 h-4 w-4 text-primary" />
                                    <div>
                                      <div className="font-medium text-foreground">Next send</div>
                                      <div>{nextRunDisplay || 'Not scheduled yet'}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Clock className="mt-0.5 h-4 w-4 text-primary" />
                                    <div>
                                      <div className="font-medium text-foreground">Last sent</div>
                                      <div>{lastSentDisplay || 'Not sent yet'}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <RefreshCw className="mt-0.5 h-4 w-4 text-primary" />
                                    <div>
                                      <div className="font-medium text-foreground">Cadence</div>
                                      <div>{scheduleDisplay}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Calendar className="mt-0.5 h-4 w-4 text-primary" />
                                    <div>
                                      <div className="font-medium text-foreground">Start date</div>
                                      <div>{startDateDisplay || 'Not set'}</div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Users className="h-4 w-4" />
                                  <span>Target: {targetUser ? `${targetUser.user.firstName} ${targetUser.user.lastName}` : 'Unknown User'}</span>
                                </div>
                              </div>
                            </div>
                            {canManageTeamMembers && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      className="gap-2"
                                      disabled={isRecurringActionPending || !hasReviewerParticipants}
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        handleSendCycleNow(cycle);
                                      }}
                                    >
                                      <Send className="h-4 w-4" />
                                      Send Cycle Now
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="gap-2"
                                      disabled={isRecurringActionPending || !hasReviewerParticipants}
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        openParticipantModal(cycle);
                                      }}
                                    >
                                      <UserPlus className="h-4 w-4" />
                                      Send a Participant
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="gap-2"
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        openEditRecurringFeedbackModal(cycle);
                                      }}
                                    >
                                      <Edit3 className="h-4 w-4" />
                                      Edit Cycle
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="gap-2"
                                      disabled={isRecurringActionPending}
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        handleToggleCycleStatus(cycle);
                                      }}
                                    >
                                      <PauseIconComponent className="h-4 w-4" />
                                      {pauseLabel}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="gap-2 text-destructive"
                                      disabled={isRecurringActionPending}
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        handleDeleteCycle(cycle);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      Delete Cycle
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-muted/30 rounded-lg p-3 border border-border">
                              <div className="text-xs text-muted-foreground mb-1">Schedule</div>
                              <div className="font-medium text-foreground text-sm">{scheduleDisplay}</div>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 border border-border">
                              <div className="text-xs text-muted-foreground mb-1">Questions</div>
                              <div className="font-medium text-foreground text-sm">{cycle.questions?.length || 0}</div>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 border border-border">
                              <div className="text-xs text-muted-foreground mb-1">Participants</div>
                              <div className="font-medium text-foreground text-sm">{cycle.participants?.length || 0}</div>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 border border-border">
                              <div className="text-xs text-muted-foreground mb-1">Next Run</div>
                              <div className="font-medium text-foreground text-sm">{new Date(cycle.nextRunDate).toLocaleDateString()}</div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                          {canViewFeedbackHistory && (
                            <div className="flex items-center gap-3">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => {
                                  setHistoryModalState({ open: true, cycle });
                                }}
                              >
                                <Eye className="h-4 w-4" />
                                View History
                              </Button>
                            </div>
                          )}
                            <div className="text-sm text-muted-foreground">
                              Created on {new Date(cycle.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="one-on-one-sessions" className="space-y-0">
              <div className="p-6 space-y-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">1-on-1 Sessions</h3>
                    <p className="text-muted-foreground">View and manage completed and scheduled 1-on-1 sessions</p>
                  </div>
                </div>

                {isLoadingSessions ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-border border-t-foreground"></div>
                      <span className="text-muted-foreground">Loading 1-on-1 sessions...</span>
                    </div>
                  </div>
                ) : oneOnOneSessions.length === 0 ? (
                  <div className="text-center py-16 bg-muted/30 rounded-lg border border-border">
                    <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-6">
                      <MessageSquare className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h4 className="text-xl font-bold text-foreground mb-2">No 1-on-1 Sessions Yet</h4>
                    <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                      Schedule regular 1-on-1 sessions to maintain open communication and support team development.
                    </p>
                    {canManageOneOnOnes && (
                      <Button className="gap-2" onClick={() => handleCreateOneOnOneSession()}>
                        <Calendar className="h-4 w-4" />
                        Schedule First Session
                      </Button>
                    )}
                  </div>
                ) : (
        <SessionsGrid
          sessions={oneOnOneSessions.map((session: any) => {
            const participantName = getMemberDisplayName(session.participantId, session.participant, 'Team member');
            const managerName = getMemberDisplayName(session.managerId, session.manager, 'Manager');
            const deliveryMethod = session.deliveryMethod === 'email' ? 'email' : 'physical';
            const typeLabel = deliveryMethod === 'email' ? 'Email 1-on-1' : 'Physical 1-on-1';

            const matchedManager = teamMembers.find((member: any) => member.userId === session.managerId || member.user?.id === session.managerId);
            const matchedParticipant = teamMembers.find((member: any) => member.userId === session.participantId || member.user?.id === session.participantId);
            const managerAvatar = session.manager?.avatar ?? matchedManager?.user?.avatar ?? matchedManager?.avatar ?? getFallbackAvatarUrl(managerName, session.manager?.email || matchedManager?.user?.email);
            const participantAvatar = session.participant?.avatar ?? matchedParticipant?.user?.avatar ?? matchedParticipant?.avatar ?? getFallbackAvatarUrl(participantName, session.participant?.email || matchedParticipant?.user?.email);

            return {
              ...session,
              title: session.title || `${typeLabel}`,
              type: typeLabel,
              completedAt: session.completedAt || session.scheduledDate,
              mood: session.mood || Math.floor(Math.random() * 2) + 4, // 4-5 for demo
              productivityRating: session.productivityRating || Math.floor(Math.random() * 2) + 4,
              teamTempRating: session.teamTempRating || Math.floor(Math.random() * 2) + 4,
              manager: {
                id: session.managerId,
                name: managerName,
                email: session.manager?.email || matchedManager?.user?.email || 'unknown@company.com',
                avatar: managerAvatar
              },
              employee: {
                id: session.participantId,
                name: participantName,
                email: session.participant?.email || matchedParticipant?.user?.email || 'unknown@company.com',
                avatar: participantAvatar
              }
            };
          })}
          isLoading={isLoadingSessions}
          onSessionAction={(session, action) => {
            if (action === 'view') {
              const conductUrl = generateConductUrl(session);
              window.open(conductUrl, '_blank', 'noopener');
            }
            if (action === 'delete') {
              setSessionToDelete(session);
            }
          }}
        />
      )}
              </div>
            </TabsContent>

            <TabsContent value="one-on-one" className="space-y-0">
              <div className="p-6 space-y-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Users className="h-4 w-4 text-blue-600" />
                      </div>
                      <h3 className="text-3xl font-bold text-foreground">1-on-1 Setup</h3>
                    </div>
                    <p className="text-lg text-muted-foreground pl-11">
                      Configure recurring 1-on-1 sessions for team members to build stronger relationships and drive development
                    </p>
                  </div>
                  {canManageOneOnOnes && (
                    <Button size="lg" className="gap-2 shadow-sm" onClick={handleCreateOneOnOneSetup}>
                      <PlusCircle className="h-5 w-5" />
                      Create Setup
                    </Button>
                  )}
                </div>

                {/* Summary Metrics Cards */}
                {!isLoadingSetups && oneOnOneSetups.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Setups</p>
                          <p className="text-2xl font-bold text-foreground">{setupMetrics.total}</p>
                        </div>
                        <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Active</p>
                          <p className="text-2xl font-bold text-green-600">{setupMetrics.active}</p>
                        </div>
                        <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Paused</p>
                          <p className="text-2xl font-bold text-yellow-600">{setupMetrics.paused}</p>
                        </div>
                        <div className="h-8 w-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Pause className="h-4 w-4 text-yellow-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Overdue</p>
                          <p className="text-2xl font-bold text-red-600">{setupMetrics.overdue}</p>
                        </div>
                        <div className="h-8 w-8 bg-red-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        </div>
                      </div>
                    </Card>
                  </div>
                )}

                {isLoadingSetups ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-border border-t-foreground"></div>
                      <span className="text-muted-foreground">Loading 1-on-1 setups...</span>
                    </div>
                  </div>
                ) : oneOnOneSetups.length === 0 ? (
                  <Card className="text-center py-16 bg-gradient-to-br from-muted/30 to-muted/10 border-2 border-dashed border-border">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                      <Calendar className="h-10 w-10 text-blue-600" />
                    </div>
                    <div className="space-y-4 max-w-2xl mx-auto">
                      <div>
                        <h4 className="text-2xl font-bold text-foreground mb-2">Start Building Stronger Teams</h4>
                        <p className="text-lg text-muted-foreground">
                          No 1-on-1 setups have been created yet
                        </p>
                      </div>
                      <div className="bg-background/60 rounded-lg p-6 text-left">
                        <h5 className="font-semibold text-foreground mb-3">Why set up 1-on-1s?</h5>
                        <ul className="text-sm text-muted-foreground space-y-2">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            Build stronger relationships between managers and team members
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            Provide regular feedback and career development guidance
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            Address challenges and celebrate achievements consistently
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            Create accountability and track progress on goals
                          </li>
                        </ul>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                        {canManageOneOnOnes ? (
                          <>
                            <Button className="gap-2" onClick={handleCreateOneOnOneSetup}>
                              <PlusCircle className="h-4 w-4" />
                              Create Your First Setup
                            </Button>
                            <Button variant="outline" className="gap-2">
                              <Eye className="h-4 w-4" />
                              Learn More
                            </Button>
                          </>
                        ) : (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                              <strong>Need 1-on-1 sessions?</strong> Contact your manager to set up recurring meetings for professional development and feedback.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 gap-6">
                    {oneOnOneSetups.map((setup: OneOnOneSetup) => {
                      const participantId = setup.participantId || (setup as any).employeeId;
                      const participantName = getMemberDisplayName(participantId, (setup as any).participant, 'Team member');
                      const managerName = getMemberDisplayName(setup.managerId, (setup as any).manager, 'Manager');
                      const workflowType = setup.workflowType === 'email' ? 'email' : setup.workflowType === 'physical' ? 'physical' : 'physical';
                      const WorkflowIcon = workflowType === 'email' ? Mail : Video;
                      const workflowLabel = workflowType === 'email' ? 'Email' : 'Physical';
                      const status = getSetupStatus(setup);
                      const statusConfig = getStatusConfig(status);
                      const StatusIcon = statusConfig.icon;
                      const sessionCount = getSessionCount(setup.id);
                      const completedSessions = oneOnOneSessions.filter((session) => session.setupId === setup.id && session.status === 'completed').length;
                      const questionCount = setup.questions?.length ?? 0;
                      const manualSendAvailable = workflowType === 'email';
                      const isSetupActive = setup.isActive !== false;
                      const toggleLabel = isSetupActive ? 'Pause Setup' : 'Resume Setup';
                      const ToggleIcon = isSetupActive ? Pause : PlayCircle;

                      return (
                        <div key={setup.id} className="bg-background p-6 rounded-lg border border-border hover:border-gray-300 transition-all duration-200 hover:shadow-md">
                          <div className="flex items-start justify-between mb-6">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${status === 'active' ? 'bg-green-100' : status === 'overdue' ? 'bg-red-100' : 'bg-yellow-100'}`}>
                                <StatusIcon className={`h-6 w-6 ${status === 'active' ? 'text-green-600' : status === 'overdue' ? 'text-red-600' : 'text-yellow-600'}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-3 mb-1">
                                  <h4 className="text-lg font-bold text-foreground">
                                    {setup.title}
                                  </h4>
                                  <Badge className={`${statusConfig.className} border gap-1`}>
                                    <StatusIcon className="h-3 w-3" />
                                    {statusConfig.label}
                                  </Badge>
                                  <Badge variant="outline" className="gap-1">
                                    <WorkflowIcon className="h-3 w-3" />
                                    {workflowLabel} 1-on-1
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                  <Users className="h-4 w-4" />
                                  <span>{participantName} & {managerName}</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <RefreshCw className="h-4 w-4" />
                                    <span>{formatFrequencyLabel(setup.frequency)}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    <span>{setup.duration} minutes</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem className="gap-2" onClick={() => handleCreateOneOnOneSession(setup)} disabled={createSessionFromSetupMutation.isPending}>
                                  <Plus className="h-4 w-4" />
                                  Create New Session
                                </DropdownMenuItem>
                                <DropdownMenuItem className="gap-2" onClick={() => handleViewSessions(setup)}>
                                  <Eye className="h-4 w-4" />
                                  View Sessions ({sessionCount})
                                </DropdownMenuItem>
                                {manualSendAvailable && (
                                  <DropdownMenuItem className="gap-2" onClick={() => handleManualSend(setup)}>
                                    <Send className="h-4 w-4" />
                                    Manual Send Scheduled 1-1
                                  </DropdownMenuItem>
                                )}
                                {canManageOneOnOnes && (
                                  <>
                                    <DropdownMenuItem className="gap-2" onClick={() => handleEditOneOnOneSetup(setup)}>
                                      <Edit3 className="h-4 w-4" />
                                      Edit Setup
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-2" onClick={() => handlePauseSetup(setup)}>
                                      <ToggleIcon className="h-4 w-4" />
                                      {toggleLabel}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="gap-2 text-destructive" onClick={() => handleDeleteOneOnOneSetup(setup)}>
                                      <Trash2 className="h-4 w-4" />
                                      Delete Setup & Sessions
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {workflowType === 'email' ? (
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Type</div>
                                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                                  <WorkflowIcon className="h-4 w-4" />
                                  {workflowLabel}
                                </div>
                              </div>
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Schedule</div>
                                <div className="font-medium text-foreground text-sm">{formatFrequencyLabel(setup.frequency)}</div>
                              </div>
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Questions</div>
                                <div className="font-medium text-foreground text-sm">{questionCount}</div>
                              </div>
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Sessions</div>
                                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                                  <Badge variant="outline" className="px-2 py-0.5 text-xs font-medium">{sessionCount} total</Badge>
                                  <Badge variant="secondary" className="gap-1 px-2 py-0.5 text-xs font-medium">
                                    <CheckCircle className="h-3 w-3" />
                                    {completedSessions}
                                  </Badge>
                                </div>
                              </div>
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Due Window</div>
                                <div className="font-medium text-foreground text-sm">{setup.emailDueDays ?? 7} day{(setup.emailDueDays ?? 7) === 1 ? '' : 's'}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Type</div>
                                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                                  <WorkflowIcon className="h-4 w-4" />
                                  {workflowLabel}
                                </div>
                              </div>
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Schedule</div>
                                <div className="font-medium text-foreground text-sm">{formatFrequencyLabel(setup.frequency)}</div>
                              </div>
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Questions</div>
                                <div className="font-medium text-foreground text-sm">{questionCount}</div>
                              </div>
                              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                                <div className="text-xs text-muted-foreground mb-1">Sessions</div>
                                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                                  <Badge variant="outline" className="px-2 py-0.5 text-xs font-medium">{sessionCount} total</Badge>
                                  <Badge variant="secondary" className="gap-1 px-2 py-0.5 text-xs font-medium">
                                    <CheckCircle className="h-3 w-3" />
                                    {completedSessions}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-muted/30 rounded-lg p-3 border border-border">
                              <div className="text-xs text-muted-foreground mb-1">Next Session</div>
                              <div className={`font-medium text-sm ${status === 'overdue' ? 'text-red-600' : 'text-foreground'}`}>
                                {formatRelativeDate(setup.nextScheduledDate ?? null)}
                              </div>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 border border-border">
                              <div className="text-xs text-muted-foreground mb-1">Default Agenda Items</div>
                              <div className="font-medium text-foreground text-sm">{setup.defaultAgenda?.length || 0}</div>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 border border-border">
                              <div className="text-xs text-muted-foreground mb-1">Created</div>
                              <div className="font-medium text-foreground text-sm">
                                {setup.createdAt ? new Date(setup.createdAt).toLocaleDateString() : 'Unknown'}
                              </div>
                            </div>
                          </div>

                          {setup.defaultAgenda && setup.defaultAgenda.length > 0 && (
                            <div className="mb-4">
                              <div className="text-sm font-medium text-foreground mb-2">Default Agenda:</div>
                              <ul className="text-sm text-muted-foreground space-y-1">
                                {setup.defaultAgenda.slice(0, 3).map((item: string, index: number) => (
                                  <li key={index}>• {item}</li>
                                ))}
                                {setup.defaultAgenda.length > 3 && (
                                  <li className="text-xs">+ {setup.defaultAgenda.length - 3} more items</li>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Modals */}
        <EditTeamModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          team={team}
          onSuccess={handleEditSuccess}
        />

        <DeleteTeamModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          team={team}
          onSuccess={handleDeleteSuccess}
        />

        {/* One-on-One Setup Modals */}
        <CreateOneOnOneSetupModal
          isOpen={isCreateOneOnOneSetupModalOpen}
          onClose={() => setIsCreateOneOnOneSetupModalOpen(false)}
          teamId={id!}
          teamMembers={teamMembers}
          onSuccess={handleOneOnOneSetupModalSuccess}
        />

        <EditOneOnOneSetupModal
          isOpen={isEditOneOnOneSetupModalOpen}
          onClose={() => setIsEditOneOnOneSetupModalOpen(false)}
          teamId={id!}
          setup={selectedOneOnOneSetup}
          teamMembers={teamMembers}
          onSuccess={handleOneOnOneSetupModalSuccess}
        />

        <DeleteOneOnOneSetupModal
          isOpen={isDeleteOneOnOneSetupModalOpen}
          onClose={() => setIsDeleteOneOnOneSetupModalOpen(false)}
          teamId={id!}
          setup={selectedOneOnOneSetup}
          teamMembers={teamMembers}
          onSuccess={handleOneOnOneSetupModalSuccess}
        />

        {/* One-on-One Session Modals */}
        <CreateOneOnOneSessionModal
          isOpen={isCreateOneOnOneSessionModalOpen}
          onClose={() => setIsCreateOneOnOneSessionModalOpen(false)}
          teamId={id!}
          teamMembers={teamMembers}
          oneOnOneSetups={oneOnOneSetups}
          preselectedSetup={preselectedSetupForSession || undefined}
          onSuccess={handleOneOnOneSessionModalSuccess}
        />

        <EditOneOnOneSessionModal
          isOpen={isEditOneOnOneSessionModalOpen}
          onClose={() => setIsEditOneOnOneSessionModalOpen(false)}
          teamId={id!}
          session={selectedOneOnOneSession}
          teamMembers={teamMembers}
          onSuccess={handleOneOnOneSessionModalSuccess}
        />

        <ViewOneOnOneSessionsModal
          isOpen={viewSessionsModal.open}
          onClose={() => setViewSessionsModal({ open: false, setup: null })}
          setup={viewSessionsModal.setup}
          sessions={sessionsForSelectedSetup}
          manager={modalManager}
          participant={modalParticipant}
        />

        <DeleteOneOnOneSessionModal
          isOpen={!!sessionToDelete}
          onClose={() => setSessionToDelete(null)}
          teamId={id!}
          session={sessionToDelete}
          onSuccess={handleOneOnOneSessionModalSuccess}
        />

        {/* Team Member Action Modals */}
        <ReassignMemberModal
          isOpen={reassignModal.open}
          onClose={() => setReassignModal({ open: false, member: null })}
          onReassign={async ({ toTeamId, ...rest }) => {
            void rest;
            if (!team?.id || !reassignModal.member?.user?.id) {
              throw new Error('Missing team or member identifier');
            }

            if (toTeamId === team.id) {
              toast.error('Please select a different team for reassignment.');
              return;
            }

            const userId = reassignModal.member.user.id;
            const currentRole = (reassignModal.member.role ?? '').toUpperCase();
            const newRole: 'MANAGER' | 'TEAM_LEAD' | 'MEMBER' =
              currentRole === 'MANAGER'
                ? 'MANAGER'
                : currentRole === 'TEAM_LEAD'
                ? 'TEAM_LEAD'
                : 'MEMBER';

            try {
              await removeTeamMemberMutation.mutateAsync({
                teamId: team.id,
                userId,
              });

              await upsertTeamMemberMutation.mutateAsync({
                teamId: toTeamId,
                userId,
                role: newRole,
              });

              toast.success('Member reassigned successfully');
            } catch (error: any) {
              console.error('Failed to reassign member:', error);
              toast.error(error?.message || 'Failed to reassign member. Please try again.');
              throw error;
            }
          }}
          member={reassignModal.member}
          currentTeam={{ id: team?.id || '', name: team?.name || '' }}
          teams={organizationTeams}
        />

        <EditRoleModal
          isOpen={editRoleModal.open}
          onClose={() => setEditRoleModal({ open: false, member: null })}
          onSave={async ({ teamRole }) => {
            if (!team?.id || !editRoleModal.member?.user?.id) {
              throw new Error('Missing team or member identifier');
            }

            const roleForApi: 'MANAGER' | 'TEAM_LEAD' | 'MEMBER' =
              teamRole === 'MANAGER' ? 'MANAGER' :
              teamRole === 'TEAM_LEAD' ? 'TEAM_LEAD' : 'MEMBER';

            await upsertTeamMemberMutation.mutateAsync({
              teamId: team.id,
              userId: editRoleModal.member.user.id,
              role: roleForApi,
            });

            toast.success('Role updated successfully');
          }}
          member={editRoleModal.member}
          teamName={team?.name || ''}
        />

        <Setup1on1Modal
          isOpen={oneOnOneModal.open}
          onClose={() => setOneOnOneModal({ open: false, member: null })}
          onSubmit={async (data) => {
            console.log('Setup 1-on-1:', data);
            toast.success('1-on-1 setup successfully');
          }}
          member={oneOnOneModal.member}
          currentUser={user}
        />

        <RemoveFromTeamModal
          isOpen={removeModal.open}
          onClose={() => setRemoveModal({ open: false, member: null })}
          onRemove={async ({ reason, notifyMember }: { reason: string; notifyMember: boolean }) => {
            void reason;
            void notifyMember;
            if (!team?.id || !removeModal.member?.user?.id) {
              throw new Error('Missing team or member identifier');
            }

            try {
              await removeTeamMemberMutation.mutateAsync({
                teamId: team.id,
                userId: removeModal.member.user.id,
              });
              toast.success('Member removed successfully');
            } catch (error: any) {
              console.error('Failed to remove member:', error);
              toast.error(error?.message || 'Failed to remove member. Please try again.');
              throw error;
            }
          }}
          member={removeModal.member}
          teamName={team?.name || ''}
        />

        <CreateRecurringFeedbackModal
          isOpen={recurringModalState.open}
          mode={recurringModalState.mode}
          initialCycle={recurringModalState.cycle || undefined}
          onClose={closeRecurringFeedbackModal}
          onSuccess={closeRecurringFeedbackModal}
          teamId={team?.id || id || ''}
          teamMembers={modalTeamMembers}
        />

        <SendRecurringFeedbackParticipantModal
          isOpen={participantModalState.open}
          cycle={participantModalState.cycle || undefined}
          onClose={closeParticipantModal}
          onSend={handleSendParticipant}
          isSending={sendRecurringParticipantMutation.isPending}
        />

        <RecurringFeedbackHistoryModal
          open={historyModalState.open}
          onOpenChange={(open) => {
            if (!open) {
              setHistoryModalState({ open: false, cycle: null });
            } else {
              setHistoryModalState((prev) => ({ ...prev, open: true }));
            }
          }}
          cycleName={historyModalState.cycle?.name}
          history={historyPayload}
          loading={historyLoadingState}
          error={historyErrorMessage}
        />

        <AddTeamMemberModal
          isOpen={addMemberModal}
          onClose={() => setAddMemberModal(false)}
          teamName={team?.name || ''}
          members={availableMembersForTeam}
          onAdd={async ({ userId, teamRole }) => {
            if (!team?.id) {
              throw new Error('Missing team identifier');
            }

            await upsertTeamMemberMutation.mutateAsync({
              teamId: team.id,
              userId,
              role: teamRole,
            });

            toast.success('Member added to team successfully');
          }}
        />

        <EditMemberModal
          member={editProfileModal.member}
          isOpen={editProfileModal.open}
          onClose={() => setEditProfileModal({ open: false, member: null })}
          context="team"
          organizationId={user?.currentOrganizationId || 'organization'}
          managers={[]}
          teams={[]}
          onSubmitOverride={async (formData) => {
            if (!team?.id || !editProfileModal.member?.user?.id) {
              throw new Error('Missing team or member identifier');
            }

            const normalizedRole = formData.role.toLowerCase();
            const roleForApi: 'MANAGER' | 'TEAM_LEAD' | 'MEMBER' =
              normalizedRole === 'manager' ? 'MANAGER' :
              normalizedRole === 'team_lead' ? 'TEAM_LEAD' : 'MEMBER';

            await upsertTeamMemberMutation.mutateAsync({
              teamId: team.id,
              userId: editProfileModal.member.user.id,
              role: roleForApi,
            });

            toast.success('Team member updated successfully');
          }}
        />
      </div>
    </div>
  );
}
